import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  useNavigate, useLocation, useParams, Navigate,
} from 'react-router';
import PageTabs from '../../components/page-tabs/page-tabs.component';
import LimitedPageContainer from '../../components/limited-page-block/limited-page-container.component';
import ContentHeader from '../../components/content-header/content-header.component';
import Loader from '../../components/loader/loader.component';
import CheckAccess from '../../components/check-access/check-access.component';
import StatusBar from '../../components/status-bar/status-bar.component';
import ContactModal from '../contact-list/contact-modal/contact-modal.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import LeaveConfirmModal from '../../components/leave-confirm-modal/leave-confirm-modal.component';
import PageError from '../../components/page-error/page-error.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import Form from '../../components/form/form.component';
import FormError from '../../components/form-error/form-error.component';
import FormContainer from '../../components/form-container/form-container.component';
import Controls from '../app/controls/controls.component';
import ContactSummary from './contact-summary/contact-summary.component';
import ContactAssociatedDeals from './contact-associated-deals/contact-associated-deals.component';
import ContactValidator from './contact.validator';
import GoogleMapService from '../../services/google-map-service';
import FormatService from '../../services/format-service';
import FormService from '../../services/form-service';
import ScreenService from '../../services/screen-service';
import StructureService from '../../services/structure-service';
import useEditModeQuery from '../../hooks/use-edit-mode-query';
import useFormBlock from '../../hooks/use-form-block';
import useFormStructureModified from '../../hooks/use-form-structure-modified';
import useServerFormErrors from '../../hooks/use-server-form-errors';
import { toggleEditMode } from '../app/controls/controls.action';
import {
  getContactData, getContactUserData, resetContactData, resetContactUserData, deleteContactComment, saveContactComment,
  saveContactData, getContactVCardData, resetContactVCardData, getContactDealListData, getContactRelatedContactListData,
} from './contact.action';
import {
  getContactCompanyByDomainData, getContactPersonByEmailData, resetContactCompanyData, resetContactPersonData,
  deleteContactList,
} from '../contact-list/contact-list.action';
import {
  getListContactCompanyData, getListContactPersonData, getListTagData, getListNotLinkedUserData,
} from '../app/search-list/search-list.action';
import { resetFormErrors } from '../app/app.action';
import { CONTACT_CREATE, CONTACT_DELETE, CONTACT_EDIT, CONTACT_VIEW } from '../../constants/entity/permissions';
import { FORM_ID_CONTACT } from '../../constants/system/form-ids';
import {
  ROUTE_CONTACT, ROUTE_CONTACT_LIST, ROUTE_NOT_FOUND, ROUTE_SEARCH, ROUTE_PARAM_ID,
} from '../../constants/system/routes';
import {
  QUERY_PARAM_TAG_ID, QUERY_PARAM_COMMENT_ID, QUERY_PARAM_TAB,
} from '../../constants/system/query-params';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../constants/entity/contact-types';
import { TAB_NAME_SUMMARY, TAB_NAME_ASSOCIATED_DEALS } from '../../constants/system/tab-names';
import { TAB_INDEX_DEFAULT } from '../../constants/system/tab-indexes';
import { TIMEOUT_SHORT } from '../../constants/system/timeouts';

const Contact: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isDataLoaded,
    isEditMode,
    isRightDrawerOpen,
    isCommentSaving,
    isContactDealListLoading,
    isRelatedContactListLoading,
    currentUser,
    contactData,
    contactPersonData,
    contactCompanyData,
    contactVCardFile,
    contactUserData,
    contactDealList,
    relatedContactList,
    contactDealsTotal,
    relatedContactsTotal,
    contactTypeList,
    contactPersonList,
    contactCompanyList,
    contactGroupList,
    assetTypeList,
    commentLabelList,
    tagList,
    contactLinkedUserList,
    notLinkedUserList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const routeParams: ReactRouterParams = useParams();
  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const queryCommentId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_COMMENT_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_COMMENT_ID].toString()) : null;
  const contactId: number = FormatService.parseInt(routeParams[ROUTE_PARAM_ID]);
  const isControlHidden: boolean = !!queryTab && queryTab !== TAB_NAME_SUMMARY;
  const isContactLinkedToUser: boolean = !!contactData && !!contactData.user;
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;
  const companyContactPersonList: Contact[] = contactPersonList && contactPersonList.length
    ? contactPersonList.filter((contactItem: Contact): boolean => (
      !contactItem.company || (contactItem.company && contactItem.company.id === contactId)
    )) : [];

  const [mainContactAddress, setMainContactAddress]: [JsonObject | null, Function] = useState(null);
  const [contactAddresses, setContactAddresses]: [Record<string, JsonObject> | null, Function] = useState(null);
  const [contactPhotoFile, setContactPhotoFile]: [File | null, Function] = useState(null);
  const [contactTags, setContactTags]: [Tag[] | null, Function] = useState(null);
  const [contactGroups, setContactGroups]: [ContactGroup[] | null, Function] = useState(null);
  const [contactAssetTypes, setContactAssetTypes]: [AssetType[] | null, Function] = useState(null);
  const [contactRelatedContacts, setContactRelatedContacts]: [Contact[] | null, Function] = useState(null);
  const [newContactAddress, setNewContactAddress]: [JsonObject | null, Function] = useState(null);
  const [newContactPhotoFile, setNewContactPhotoFile]: [File | null, Function] = useState(null);
  const [newContactTags, setNewContactTags]: [Tag[] | null, Function] = useState(null);
  const [newContactAssetTypes, setNewContactAssetTypes]: [AssetType[] | null, Function] = useState(null);
  const [newContactGroups, setNewContactGroups]: [ContactGroup[] | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [lastTabIndex, setLastTabIndex]: [number | null, Function] = useState(null);
  const [contactType, setContactType]: [string | null, Function] = useState(null);
  const [isContactPhotoDeleted, setIsContactPhotoDeleted]: [boolean, Function] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen]: [boolean, Function] = useState(false);
  const [contactPhotoPreviewUrl, setContactPhotoPreviewUrl]: [string | null, Function] = useState(null);
  const [newContactPhotoPreviewUrl, setNewContactPhotoPreviewUrl]: [string | null, Function] = useState(null);
  const [contactAddressItems, setContactAddressItems]: [Record<string, ContactAddressInput>, Function]
    = useState({});
  const [contactAddressNewItemIds, setContactAddressNewItemIds]: [number[], Function] = useState([]);
  const [contactAddressDetailsItems, setContactAddressDetailsItems]: [number[], Function] = useState([]);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);

  const saveFormDeps: DepsItem[] = useMemo((): DepsItem[] => [formValues], [formValues]);
  const contactViewPermissions: string[] = useMemo((): string[] => [CONTACT_VIEW], []);
  const contactEditPermissions: string[] = useMemo((): string[] => [CONTACT_EDIT], []);

  const contactUserId: number = contactUserData && contactUserData.id;
  const isFormDataPristine: boolean = isFormPristine && !contactPhotoFile
    && !contactTags && !contactAssetTypes && !contactRelatedContacts && !contactGroups && !isContactPhotoDeleted
    && !newContactPhotoFile && !newContactAssetTypes && !newContactTags && !newContactGroups;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && (!!lastLocation || (lastTabIndex !== null
    && lastTabIndex >= TAB_INDEX_DEFAULT));
  const pageTitle: string = `Contact${contactData && `: ${contactData.fullName}`}`;
  const createButtonTitle: string = (!queryTab || (queryTab && queryTab === TAB_NAME_SUMMARY))
  && contactType === CONTACT_TYPE_COMPANY ? 'New Staff Member' : '';
  const formPrefix: string = 'contactData';
  const formPrefixNewContact: string = 'contactData.newContact';
  const deleteConfirmModalTitle: string = 'Delete Contact';
  const deleteConfirmModalText: string = 'Are you sure you want to delete this contact?';

  const onEditModeButtonClick: Function = useCallback((): void => {
    dispatch(toggleEditMode(!isEditMode));
  }, [isEditMode]);

  const onContactCompanySearch: Function = useCallback((query: string): void => {
    dispatch(getListContactCompanyData({
      query,
    }));
  }, []);

  const onContactCompanySearchClose: Function = useCallback((): void => {
    dispatch(getListContactCompanyData({
      query: null,
    }));
  }, []);

  const onCancelForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);

    setMainContactAddress(null);
    setContactAddresses(null);
    setContactPhotoFile(null);
    setContactTags(null);
    setContactAssetTypes(null);
    setContactRelatedContacts(null);
    setContactGroups(null);
    setContactPhotoPreviewUrl(null);
    setContactAddressNewItemIds([]);
    setIsContactPhotoDeleted(false);

    setNewContactAddress(null);
    setNewContactPhotoFile(null);
    setNewContactTags(null);
    setNewContactAssetTypes(null);
    setNewContactGroups(null);
    setNewContactPhotoPreviewUrl(null);

    setErrorMessage(null);

    dispatch(toggleEditMode(false));

    dispatch(resetContactPersonData());
    dispatch(resetContactCompanyData());
    dispatch(resetContactVCardData());
  }, []);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if (isEditMode) {
      dispatch(toggleEditMode(false));
    }

    return true;
  }, [isFormChanged, isEditMode]);

  const onBeforeTabChange: Function = useCallback((newTabIndex: number): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastTabIndex(newTabIndex);
    }
  }, [isFormChanged]);

  const onContactPersonSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactPersonData({
      query,
    }));
  }, []);

  const onContactPersonSearchClose: Function = useCallback((): void => {
    dispatch(getListContactPersonData({
      query: null,
    }));
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onContactAddressAddNewItem: Function = useCallback((): void => {
    const newContactAddressItemId: number = StructureService.getNewItemId(
      [],
      (contactAddressItems && Object.values(contactAddressItems)) || [],
    );
    const formKeyContactAddressItem: string = FormService.getIndexedFormFieldKey(newContactAddressItemId);

    const newContactAddressItem: ContactAddressInput = {
      id: newContactAddressItemId,
      address: null,
      addressDetails: null,
      name: null,
    };

    if (newContactAddressItem.id) {
      setContactAddressItems({
        ...(contactAddressItems || {}),
        [newContactAddressItem.id]: newContactAddressItem,
      });

      setContactAddressNewItemIds([
        ...contactAddressNewItemIds,
        newContactAddressItem.id,
      ]);
    }

    changeForm(`${formPrefix}.addresses[${formKeyContactAddressItem}]`, newContactAddressItem);
  }, [contactAddressItems, contactAddressNewItemIds]);

  const onContactAddressDeleteItemClick: Function = useCallback((contactAddressItemId: number): void => {
    const formKeyContactAddressItem: string = FormService.getIndexedFormFieldKey(contactAddressItemId);

    if (contactAddressItems) {
      const newContactAddressItems: Record<string, ContactAddressInput> = { ...contactAddressItems };
      delete newContactAddressItems[contactAddressItemId];
      setContactAddressItems({ ...newContactAddressItems });
    }

    if (contactAddressNewItemIds.length) {
      const contactAddressNewItemIdIndex: number = contactAddressNewItemIds.indexOf(contactAddressItemId);

      if (contactAddressNewItemIdIndex !== FormatService.INDEX_NOT_FOUND) {
        StructureService.deleteListItemByIndex(contactAddressNewItemIds, contactAddressNewItemIdIndex);
      }

      setContactAddressNewItemIds([...contactAddressNewItemIds]);
    }

    changeForm(`${formPrefix}.addresses[${formKeyContactAddressItem}]`, null);
  }, [contactAddressItems, contactAddressNewItemIds]);

  const onContactAddressDetailsOpen: Function = useCallback((contactAddressId: number): void => {
    if (contactAddressDetailsItems) {
      const contactAddressDetailsIdIndex: number = contactAddressDetailsItems.indexOf(contactAddressId);

      if (contactAddressDetailsIdIndex === FormatService.INDEX_NOT_FOUND) {
        setContactAddressDetailsItems([...contactAddressDetailsItems, contactAddressId]);
      }
    }
  }, [contactAddressDetailsItems]);

  const onContactAddressDetailsClose: Function = useCallback((contactAddressId: number): void => {
    if (contactAddressDetailsItems) {
      const contactAddressDetailsIdIndex: number = contactAddressDetailsItems.indexOf(contactAddressId);

      if (contactAddressDetailsIdIndex !== FormatService.INDEX_NOT_FOUND) {
        StructureService.deleteListItemByIndex(contactAddressDetailsItems, contactAddressDetailsIdIndex);
      }

      setContactAddressDetailsItems([...contactAddressDetailsItems]);
    }
  }, [contactAddressDetailsItems]);

  const onContactVCardDownloadButtonClick: Function = useCallback((): void => {
    if (contactId) {
      dispatch(getContactVCardData({
        contactId,
      }));
    }
  }, [contactId]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const contactFormItem: FormValueItem = {
      ...values.contactData,
    };

    delete contactFormItem.newContact;
    delete contactFormItem.location;
    delete contactFormItem.streetAddress;
    delete contactFormItem.postalCode;

    contactFormItem.userId = contactFormItem.userId || null;
    contactFormItem.companyId = contactFormItem.companyId || null;
    contactFormItem.foundationYear = contactFormItem.foundationYear
      ? FormatService.parseInt(contactFormItem.foundationYear) : null;
    contactFormItem.phone = contactFormItem.phone && FormatService.isValidPhoneNumber(contactFormItem.phone, true)
      ? FormatService.formatPhoneValue(contactFormItem.phone) : null;
    contactFormItem.phoneExtension = contactFormItem.phoneExtension
      ? FormatService.parseInt(contactFormItem.phoneExtension) : null;
    contactFormItem.phoneWork = contactFormItem.phoneWork
    && FormatService.isValidPhoneNumber(contactFormItem.phoneWork, true)
      ? FormatService.formatPhoneValue(contactFormItem.phoneWork) : null;
    contactFormItem.phoneWorkExtension = contactFormItem.phoneWorkExtension
      ? FormatService.parseInt(contactFormItem.phoneWorkExtension) : null;
    contactFormItem.groupIds = contactFormItem.groupIds && contactFormItem.groupIds.length
      ? contactFormItem.groupIds.map((contactGroupItem: ContactGroup): number => contactGroupItem.id) : [];
    contactFormItem.tags = contactFormItem.tags && contactFormItem.tags.length
      ? contactFormItem.tags.map((tagItem: Tag): string => tagItem.name) : [];
    contactFormItem.assetTypes = contactFormItem.assetTypes && contactFormItem.assetTypes.length
      ? contactFormItem.assetTypes.map((assetTypeItem: AssetType): number => assetTypeItem.id) : [];
    contactFormItem.relatedContactIds = contactFormItem.relatedContactIds && contactFormItem.relatedContactIds.length
      ? contactFormItem.relatedContactIds.map((contactItem: Contact): number => contactItem.id) : [];

    const contactAddressItems: ContactAddressInput[] = [];
    if (contactFormItem.addresses && Object.keys(contactFormItem.addresses).length) {
      Object.keys(contactFormItem.addresses).forEach((contactAddressIndex: string): void => {
        const contactAddressItem: FormValueItem = { ...contactFormItem.addresses[contactAddressIndex] };

        if (contactAddressItem && contactAddressItem.id) {
          const isNewItem: boolean = contactAddressNewItemIds && contactAddressNewItemIds.length
            ? contactAddressNewItemIds.includes(contactAddressItem.id as number) : false;

          delete contactAddressItem.location;
          delete contactAddressItem.streetAddress;
          delete contactAddressItem.postalCode;

          if (!contactAddressItem.addressDetails) {
            contactAddressItem.addressDetails = null;
          }

          if (isNewItem) {
            delete contactAddressItem.id;
          }

          contactAddressItems.push(contactAddressItem);
        }
      });
    }
    contactFormItem.addresses = contactAddressItems;

    if (contactPhotoFile) {
      contactFormItem.photo = contactPhotoFile;
    } else if (isContactPhotoDeleted) {
      contactFormItem.photo = null;
    } else {
      delete contactFormItem.photo;
    }

    const contactItem: ContactInput = contactFormItem;

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveContactData({
        contact: contactItem,
      }, (response: ActionResponse): void => {
        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [contactPhotoFile, isContactPhotoDeleted, contactAddressNewItemIds]);

  const openDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(true);
  }, []);

  const closeDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
  }, []);

  const onDeleteClick: Function = useCallback((): void => {
    openDeleteConfirmModal();
  }, []);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!contactId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteContactList({
        contactIds: [contactId],
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        setTimeout((): void => {
          navigate({
            pathname: ROUTE_CONTACT_LIST,
          });
        }, TIMEOUT_SHORT);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [contactId]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [contactPhotoFile, isContactPhotoDeleted, contactAddressNewItemIds, formValues, withFormErrors]);

  const onPhoneChange: Function = useCallback((phoneValue?: string): void => {
    changeForm('contactData.phone', phoneValue);
  }, []);

  const onPhoneWorkChange: Function = useCallback((phoneWorkValue?: string): void => {
    changeForm('contactData.phoneWork', phoneWorkValue);
  }, []);

  const onAddressSearchCompleted: Function = useCallback((
    addressObject: JsonObject, contactAddressId?: number,
  ): void => {
    if (contactAddressId) {
      setContactAddresses({
        [contactAddressId]: { ...addressObject },
      });
    } else {
      setMainContactAddress({ ...addressObject });
    }

    const formKeyContactAddress: string | null = contactAddressId
      ? FormService.getIndexedFormFieldKey(contactAddressId) : null;
    const formPrefix: string = formKeyContactAddress
      ? `contactData.addresses[${formKeyContactAddress}]` : 'contactData';

    changeForm(`${formPrefix}.address`, addressObject);
    changeForm(`${formPrefix}.location`, GoogleMapService.extractLocation(addressObject));
    changeForm(`${formPrefix}.postalCode`, GoogleMapService.extractPostalCode(addressObject));
    changeForm(`${formPrefix}.streetAddress`, GoogleMapService.extractStreetAddress(addressObject));
  }, []);

  const onAddressFieldKeyDown: Function = useCallback((contactAddressId?: number): void => {
    const formKeyContactAddress: string | null = contactAddressId
      ? FormService.getIndexedFormFieldKey(contactAddressId) : null;
    const formPrefix: string = formKeyContactAddress
      ? `contactData.addresses[${formKeyContactAddress}]` : 'contactData';

    if ((contactAddressId && contactAddresses && contactAddresses[contactAddressId])
      || (!contactAddressId && mainContactAddress)) {
      changeForm(`${formPrefix}.address`, '');
      changeForm(`${formPrefix}.streetAddress`, '');
      changeForm(`${formPrefix}.location`, '');
      changeForm(`${formPrefix}.postalCode`, '');

      if (contactAddressId) {
        setContactAddresses({
          [contactAddressId]: null,
        });
      } else {
        setMainContactAddress(null);
      }
    }
  }, [mainContactAddress, contactAddresses]);

  const onWebsiteChange: Function = useCallback((website: string): void => {
    const domain: string | null = (FormatService.isValidDomain(website) && website)
      || (FormatService.isValidLink(website) ? FormatService.extractWebsiteDomain(website) : null);

    if (!domain) {
      return;
    }

    dispatch(getContactCompanyByDomainData({
      domain,
      contactId,
    }));
  }, [contactId, contactCompanyData]);

  const onEmailChange: Function = useCallback((email: string): void => {
    if (!email || !FormatService.isValidEmail(email)) {
      return;
    }

    dispatch(getContactPersonByEmailData({
      email,
      contactId,
    }));
  }, [contactId, contactPersonData]);

  const onContactConnectByEmail: Function = useCallback((event: React.SyntheticEvent): void => {
    if (!contactPersonData) {
      return;
    }

    populateContactData(contactPersonData, formPrefix);

    dispatch(resetContactPersonData());

    event.preventDefault();
  }, [contactPersonData, formPrefix]);

  const onContactConnectByWebsite: Function = useCallback((event: React.SyntheticEvent): void => {
    if (!contactCompanyData) {
      return;
    }

    populateContactData(contactCompanyData, formPrefix);

    dispatch(resetContactCompanyData());

    event.preventDefault();
  }, [contactCompanyData, formPrefix]);

  const onNewContactPhoneChange: Function = useCallback((contactItemId: number, phoneValue?: string): void => {
    changeForm('contactData.newContact.phone', phoneValue);
  }, []);

  const onNewContactPhoneWorkChange: Function = useCallback((contactItemId: number, phoneWorkValue?: string): void => {
    changeForm('contactData.newContact.phoneWork', phoneWorkValue);
  }, []);

  const onNewContactAddressSearchCompleted: Function = useCallback((addressObject: JsonObject): void => {
    setNewContactAddress({ ...addressObject });

    changeForm('contactData.newContact.address', addressObject);
    changeForm('contactData.newContact.location', GoogleMapService.extractLocation(addressObject));
    changeForm('contactData.newContact.postalCode', GoogleMapService.extractPostalCode(addressObject));
    changeForm('contactData.newContact.streetAddress', GoogleMapService.extractStreetAddress(addressObject));
  }, []);

  const onNewContactAddressFieldKeyDown: Function = useCallback((): void => {
    if (newContactAddress) {
      changeForm('contactData.newContact.address', '');
      changeForm('contactData.newContact.streetAddress', '');
      changeForm('contactData.newContact.location', '');
      changeForm('contactData.newContact.postalCode', '');

      setNewContactAddress(null);
    }
  }, [newContactAddress]);

  const onContactPhotoFileChange: Function = useCallback((file: File): void => {
    setIsContactPhotoDeleted(false);
    setContactPhotoFile(file);
    changeForm('contactData.photo', file);
  }, []);

  const onNewContactPhotoFileChange: Function = useCallback((contactItemId: number, file: File): void => {
    setNewContactPhotoFile(file);
    changeForm('contactData.newContact.photo', file);
  }, []);

  const onContactPhotoFileDelete: MouseEventHandlerFunction = useCallback((): void => {
    setIsContactPhotoDeleted(true);
    setContactPhotoFile(null);
    changeForm('contactData.photo', null);
  }, []);

  const onContactGroupsChange: Function = useCallback((groups: ContactGroup[]): void => {
    setContactGroups(groups);
    changeForm('contactData.groupIds', groups);
  }, []);

  const onAssetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    setContactAssetTypes(assetTypeValues);
    changeForm('contactData.assetTypes', assetTypeValues);
  }, []);

  const onTagsChange: Function = useCallback((tags: Tag[]): void => {
    setContactTags(tags);
    changeForm('contactData.tags', tags);
  }, []);

  const onNewContactPhotoFileDelete: Function = useCallback((): void => {
    setNewContactPhotoFile(null);
    changeForm('contactData.newContact.photo', null);
  }, []);

  const onNewContactGroupsChange: Function = useCallback((contactItemId: number, groups: ContactGroup[]): void => {
    setNewContactGroups({
      ...(newContactGroups || {}),
      [contactItemId]: groups,
    });
    changeForm('contactData.newContact.groupIds', groups);
  }, [newContactGroups]);

  const onNewContactAssetTypesChange: Function = useCallback((
    contactItemId: number, assetTypeValues: AssetType[],
  ): void => {
    setNewContactAssetTypes(assetTypeValues);
    changeForm('contactData.newContact.assetTypes', assetTypeValues);
  }, []);

  const onNewContactTagsChange: Function = useCallback((contactItemId: number, tags: Tag[]): void => {
    setNewContactTags(tags);
    changeForm('contactData.newContact.tags', tags);
  }, []);

  const openContactModal: Function = useCallback((): void => {
    setIsContactModalOpen(true);

    changeForm('contactData.newContact.name', null);
    changeForm('contactData.newContact.firstName', null);
    changeForm('contactData.newContact.lastName', null);
    changeForm('contactData.newContact.groupIds', null);
    changeForm('contactData.newContact.companyId', contactData.id);
    changeForm('contactData.newContact.type', CONTACT_TYPE_PERSON);

    if (contactData.phone) {
      changeForm('contactData.newContact.phone', contactData.phone);
    }

    if (contactData.address) {
      onNewContactAddressSearchCompleted({ ...contactData.address });
    }
  }, [contactData]);

  const closeContactModal: Function = useCallback((): void => {
    setIsContactModalOpen(false);
  }, []);

  const onCreateButtonClick: Function = useCallback((): void => {
    openContactModal();
  }, [contactData]);

  const sendNewContactForm: Function = useCallback((
    values: FormValues, successCallback?: Function,
  ): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const newContactFormItem: FormValueItem = {
      ...values.contactData.newContact,
    };

    if (!newContactFormItem) {
      return null;
    }

    delete newContactFormItem.location;
    delete newContactFormItem.streetAddress;
    delete newContactFormItem.postalCode;

    newContactFormItem.userId = newContactFormItem.userId || null;
    newContactFormItem.companyId = newContactFormItem.companyId || null;
    newContactFormItem.foundationYear = newContactFormItem.foundationYear
      ? FormatService.parseInt(newContactFormItem.foundationYear) : null;
    newContactFormItem.phone = newContactFormItem.phone
    && FormatService.isValidPhoneNumber(newContactFormItem.phone, true)
      ? FormatService.formatPhoneValue(newContactFormItem.phone) : null;
    newContactFormItem.phoneExtension = newContactFormItem.phoneExtension
      ? FormatService.parseInt(newContactFormItem.phoneExtension) : null;
    newContactFormItem.phoneWork = newContactFormItem.phoneWork
    && FormatService.isValidPhoneNumber(newContactFormItem.phoneWork, true)
      ? FormatService.formatPhoneValue(newContactFormItem.phoneWork) : null;
    newContactFormItem.phoneWorkExtension = newContactFormItem.phoneWorkExtension
      ? FormatService.parseInt(newContactFormItem.phoneWorkExtension) : null;
    newContactFormItem.groupIds = newContactFormItem.groupIds && newContactFormItem.groupIds.length
      ? newContactFormItem.groupIds.map((contactGroupItem: ContactGroup): number => contactGroupItem.id) : [];
    newContactFormItem.tags = newContactFormItem.tags && newContactFormItem.tags.length
      ? newContactFormItem.tags.map((tagItem: Tag): string => tagItem.name) : [];
    newContactFormItem.assetTypes = newContactFormItem.assetTypes && newContactFormItem.assetTypes.length
      ? newContactFormItem.assetTypes.map((assetTypeItem: AssetType): number => assetTypeItem.id) : [];

    delete newContactFormItem.relatedContactIds;

    if (newContactPhotoFile) {
      newContactFormItem.photo = newContactPhotoFile;
    } else {
      delete newContactFormItem.photo;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveContactData({
        contact: newContactFormItem,
        contactId: contactData.id,
      }, (response: ActionResponse): void => {
        if (isContactModalOpen) {
          closeContactModal();
        }

        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        if (successCallback) {
          successCallback();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [newContactPhotoFile, contactData, isContactModalOpen]);

  const onNewContactSubmitForm: Function = useCallback((successCallback?: Function): void => {
    submitForm();

    if (!withFormErrors) {
      sendNewContactForm(formValues, successCallback);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [newContactPhotoFile, contactData, isContactModalOpen, withFormErrors, formValues]);

  const onContactTypeChange: Function = useCallback((contactTypeValue: ContactType): void => {
    setContactType(contactTypeValue);
  }, []);

  const onAddComment: Function = useCallback((
    comment: string, attachmentFiles: File[], label?: string, mentionContactIds?: number[], onSuccess?: Function,
  ): void => {
    dispatch(saveContactComment({
      comment, attachmentFiles, label, contactId, mentionContactIds,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, [contactId]);

  const onUpdateComment: Function = useCallback((
    comment: string, attachments: AttachmentInput[], attachmentFiles: File[], label?: string, commentId?: number,
    mentionContactIds?: number[], onSuccess?: Function,
  ): void => {
    dispatch(saveContactComment({
      comment, attachments, attachmentFiles, label, contactId, commentId, mentionContactIds,
    }, (): void => {
      if (onSuccess) {
        onSuccess(commentId);
      }
    }));
  }, [contactId]);

  const onDeleteComment: Function = useCallback((commentId: number, onSuccess?: Function): void => {
    dispatch(deleteContactComment({
      contactId,
      commentId,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, [contactId]);

  const onCommentEntityLinkClick: Function = useCallback((link: string): void => {
    navigate(link);
  }, []);

  const onNotLinkedUserSearch: Function = useCallback((query: string, searchOptions: SearchOptions): void => {
    dispatch(getListNotLinkedUserData({
      query,
      userId: (searchOptions && searchOptions.userId) || null,
    }));
  }, []);

  const onNotLinkedUserSearchClose: Function = useCallback((searchOptions: SearchOptions): void => {
    dispatch(getListNotLinkedUserData({
      query: null,
      userId: (searchOptions && searchOptions.userId) || null,
    }));
  }, []);

  const onNewContactCreate: Function = useCallback((successCallback?: Function): void => {
    onNewContactSubmitForm(successCallback);
  }, [newContactPhotoFile, contactData, isContactModalOpen, formValues]);

  const onRelatedContactsChange: Function = useCallback((relatedContacts: Contact[]): void => {
    setContactRelatedContacts(relatedContacts);
    changeForm('contactData.relatedContactIds', relatedContacts);
  }, []);

  const onNewContactEmailChange: Function = useCallback((contactItemId: number, email?: string): void => {
    if (!email || !FormatService.isValidEmail(email)) {
      return;
    }

    dispatch(getContactPersonByEmailData({
      email,
      contactId: contactItemId,
    }));
  }, [contactPersonData]);

  const onContactSubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const onUserChange: Function = useCallback((userId: number): void => {
    if (userId.toString() !== '') {
      dispatch(getContactUserData({
        userId,
      }));
    } else {
      dispatch(resetContactUserData());
    }
  }, []);

  const resetContactUserForm: Function = useCallback((contactUser: User): void => {
    changeForm('contactData.userId', contactUser.id);
    changeForm('contactData.firstName', contactUser.firstName);
    changeForm('contactData.lastName', contactUser.lastName);
    changeForm('contactData.email', contactUser.email || null);
    changeForm('contactData.phone', contactUser.phone || null);
    changeForm('contactData.phoneExtension', contactUser.phoneExtension || null);
    changeForm('contactData.phoneWork', contactUser.phoneWork || null);
    changeForm('contactData.phoneWorkExtension', contactUser.phoneWorkExtension || null);
    changeForm('contactData.address', contactUser.address || null);
    changeForm('contactData.streetAddress', contactUser.address
      ? GoogleMapService.extractStreetAddress(contactUser.address) : null);
    changeForm('contactData.location', contactUser.address
      ? GoogleMapService.extractLocation(contactUser.address) : null);
    changeForm('contactData.postalCode', contactUser.address
      ? GoogleMapService.extractPostalCode(contactUser.address) : null);

    setMainContactAddress(contactUser.address ? { ...contactUser.address } : null);

    if (contactUser.addressDetails && !contactData.addressDetails) {
      changeForm('contactData.addressDetails', contactUser.addressDetails);
    }

    if (contactUser.company && contactUser.company.id) {
      changeForm('contactData.companyId', contactUser.company.id);
    }

    if (contactUser.bio && !contactData.description) {
      changeForm('contactData.description', contactUser.bio);
    }

    if (contactUser.assetTypes && contactUser.assetTypes.length) {
      changeForm('contactData.assetTypes', contactUser.assetTypes);
      setContactAssetTypes(contactUser.assetTypes);
    }

    if (contactUser.title) {
      changeForm('contactData.title', contactUser.title);
    }
  }, [contactData]);

  const populateContactData: Function = useCallback((
    contactItem: Contact, prefix: string, isNewContact: boolean,
  ): void => {
    if (contactItem.firstName) {
      changeForm(`${prefix}.firstName`, contactItem.firstName);
    }

    if (contactItem.lastName) {
      changeForm(`${prefix}.lastName`, contactItem.lastName);
    }

    if (contactItem.name) {
      changeForm(`${prefix}.name`, contactItem.name);
    }

    if (contactItem.photo) {
      changeForm(`${prefix}.photoLink`, contactItem.photo);

      if (isNewContact) {
        setNewContactPhotoPreviewUrl(contactItem.photo);
      } else {
        setContactPhotoPreviewUrl(contactItem.photo);
      }
    }

    if (contactItem.phone) {
      changeForm(`${prefix}.phone`, contactItem.phone);
      changeForm(`${prefix}.phoneExtension`, contactItem.phoneExtension || null);
    }

    if (contactItem.phoneWork) {
      changeForm(`${prefix}.phoneWork`, contactItem.phoneWork);
      changeForm(`${prefix}.phoneWorkExtension`, contactItem.phoneWorkExtension || null);
    }

    if (contactItem.address) {
      changeForm(`${prefix}.address`, contactItem.address);
      changeForm(`${prefix}.location`, GoogleMapService.extractLocation(contactItem.address));
      changeForm(`${prefix}.postalCode`, GoogleMapService.extractPostalCode(contactItem.address));
      changeForm(`${prefix}.streetAddress`, GoogleMapService.extractStreetAddress(contactItem.address));
    }

    if (contactItem.linkedin) {
      changeForm(`${prefix}.linkedin`, contactItem.linkedin);
    }

    if (contactItem.description) {
      changeForm(`${prefix}.description`, contactItem.description);
    }

    if (contactItem.title) {
      changeForm(`${prefix}.title`, contactItem.title);
    }

    if (contactItem.industry) {
      changeForm(`${prefix}.industry`, contactItem.industry);
    }

    if (contactItem.foundationYear) {
      changeForm(`${prefix}.foundationYear`, contactItem.foundationYear);
    }
  }, []);

  const onTagClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tagId: number,
  ): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_TAG_ID]: tagId,
    };

    navigate({
      pathname: ROUTE_SEARCH,
      search: FormatService.formatQueryParams(queryParams),
    });

    event.stopPropagation();
  }, []);

  const onTagAddClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tagName: string,
  ): void => {
    formValues.contactData.tags.push({
      id: StructureService.getNewItemId(formValues.contactData.tags),
      name: tagName,
    });

    onTagsChange(formValues.contactData.tags);

    sendForm(formValues);

    event.stopPropagation();
  }, [contactPhotoFile, isContactPhotoDeleted, contactAddressNewItemIds, formValues]);

  const onTagDeleteClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tagId: number,
  ): void => {
    const tags: Tag[] = formValues && formValues.contactData ? formValues.contactData.tags : [];

    onTagsChange(StructureService.deleteListItemById(tags, tagId));

    sendForm(formValues);

    event.stopPropagation();
  }, [contactPhotoFile, isContactPhotoDeleted, contactAddressNewItemIds, formValues]);

  const onContactCompanyClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  const onContactRowClick: Function = useCallback((contactId: number): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection) {
      navigate({
        pathname: `${ROUTE_CONTACT}/${contactId}`,
        search: FormatService.formatQueryParams({
          [QUERY_PARAM_TAB]: TAB_NAME_SUMMARY,
        }),
      });
    }
  }, []);

  const onRefresh: Function = useCallback((): void => {
    if (contactId) {
      dispatch(getContactData({
        contactId,
      }, undefined, (exception: ServerError): void => {
        if (exception) {
          setErrorMessage(exception.message);
        }
      }));
    }
  }, [contactId]);

  const onTagSearch: Function = useCallback((query: string): void => {
    dispatch(getListTagData({
      query,
    }));
  }, []);

  const onTagSearchClose: Function = useCallback((): void => {
    dispatch(getListTagData({
      query: null,
    }));
  }, []);

  const onContactDealListLoadBatchData: Function = useCallback((options: RequestOptions): void => {
    dispatch(getContactDealListData(options));
  }, []);

  const onRelatedContactListLoadBatchData: Function = useCallback((options: RequestOptions): void => {
    dispatch(getContactRelatedContactListData(options));
  }, []);

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_SUMMARY,
      title: 'Summary',
      component: (
        <ContactSummary
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isCommentSaving={isCommentSaving}
          isContactLinkedToUser={isContactLinkedToUser}
          isContactPhotoDeleted={isContactPhotoDeleted}
          isRelatedContactListLoading={isRelatedContactListLoading}
          formError={formError}
          queryCommentId={queryCommentId}
          contactData={contactData}
          contactPersonData={contactPersonData}
          contactCompanyData={contactCompanyData}
          contactUserData={contactUserData}
          contactPhotoFile={contactPhotoFile}
          contactPhotoPreviewUrl={contactPhotoPreviewUrl}
          contactType={contactType}
          contactAddressItems={contactAddressItems}
          contactAddressNewItemIds={contactAddressNewItemIds}
          contactAddressDetailsItems={contactAddressDetailsItems}
          contactAssetTypes={contactAssetTypes}
          contactTypeList={contactTypeList}
          companyContactPersonList={companyContactPersonList}
          contactCompanyList={contactCompanyList}
          contactGroupList={contactGroupList}
          commentLabelList={commentLabelList}
          contactLinkedUserList={contactLinkedUserList}
          currentUser={currentUser}
          relatedContactList={relatedContactList}
          relatedContactsTotal={relatedContactsTotal}
          assetTypeList={assetTypeList}
          tagList={tagList}
          notLinkedUserList={notLinkedUserList}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onTagClick={onTagClick}
          onTagAddClick={onTagAddClick}
          onTagDeleteClick={onTagDeleteClick}
          onTagsChange={onTagsChange}
          onTagSearch={onTagSearch}
          onTagSearchClose={onTagSearchClose}
          onUserChange={onUserChange}
          onAddComment={onAddComment}
          onUpdateComment={onUpdateComment}
          onDeleteComment={onDeleteComment}
          onCommentEntityLinkClick={onCommentEntityLinkClick}
          onAssetTypesChange={onAssetTypesChange}
          onPhoneChange={onPhoneChange}
          onPhoneWorkChange={onPhoneWorkChange}
          onAddressSearchCompleted={onAddressSearchCompleted}
          onAddressFieldKeyDown={onAddressFieldKeyDown}
          onEmailChange={onEmailChange}
          onWebsiteChange={onWebsiteChange}
          onContactConnectByEmail={onContactConnectByEmail}
          onContactConnectByWebsite={onContactConnectByWebsite}
          onContactTypeChange={onContactTypeChange}
          onContactGroupsChange={onContactGroupsChange}
          onContactPhotoFileChange={onContactPhotoFileChange}
          onContactPhotoFileDelete={onContactPhotoFileDelete}
          onContactRowClick={onContactRowClick}
          onContactCompanyClick={onContactCompanyClick}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onNotLinkedUserSearch={onNotLinkedUserSearch}
          onNotLinkedUserSearchClose={onNotLinkedUserSearchClose}
          onEditModeButtonClick={onEditModeButtonClick}
          onCreateButtonClick={onCreateButtonClick}
          onRelatedContactsChange={onRelatedContactsChange}
          onRelatedContactListLoadBatchData={onRelatedContactListLoadBatchData}
          onContactPersonSearch={onContactPersonSearch}
          onContactPersonSearchClose={onContactPersonSearchClose}
          onContactAddressAddNewItem={onContactAddressAddNewItem}
          onContactAddressDeleteItemClick={onContactAddressDeleteItemClick}
          onContactAddressDetailsOpen={onContactAddressDetailsOpen}
          onContactAddressDetailsClose={onContactAddressDetailsClose}
          onContactVCardDownloadButtonClick={onContactVCardDownloadButtonClick}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_ASSOCIATED_DEALS,
      title: 'Associated Deals',
      component: (
        <ContactAssociatedDeals
          isLoading={isContactDealListLoading}
          contactData={contactData}
          contactDealList={contactDealList}
          contactDealsTotal={contactDealsTotal}
          onLoadBatchData={onContactDealListLoadBatchData}
        />
      ),
    },
  ];

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        navigate({
          pathname: `${ROUTE_CONTACT}/${contactId}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [locationQueryParams, tabs]);

  const onResetContactData: Function = useCallback((): void => {
    dispatch(resetContactData());
  }, []);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): ReactUseEffectCleanUp => {
    if (isDataLoaded) {
      onResetContactData();
    }

    if (contactId) {
      dispatch(getContactData({
        contactId,
      }, undefined, (exception: ServerError): void => {
        if (exception) {
          setErrorMessage(exception.message);
        }
      }));
    }

    return (): void => {
      onResetContactData();
    };
  }, [contactId]);

  useEffect((): void => {
    if (contactData) {
      if (!mainContactAddress) {
        setMainContactAddress(contactData.address || null);
      }

      if (!contactAddresses) {
        const initialContactAddresses: Record<string, JsonObject> = {};
        if (contactData.addresses && contactData.addresses.length) {
          contactData.addresses.forEach((contactAddressItem: ContactAddress): void => {
            initialContactAddresses[contactAddressItem.id] = contactAddressItem.address;
          });
        }
        setContactAddresses(initialContactAddresses);
      }
    }
  }, [contactData, isEditMode]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastTabIndex !== null && lastTabIndex >= TAB_INDEX_DEFAULT) {
        const queryParams: LocationQueryParams = locationQueryParams || {};
        const currentTab: TabItem = tabs[lastTabIndex];

        if (currentTab) {
          queryParams[QUERY_PARAM_TAB] = currentTab.name;

          navigate({
            pathname: `${ROUTE_CONTACT}/${contactId}`,
            search: FormatService.formatQueryParams(queryParams),
          });
        }

        setLastTabIndex(null);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (lastLocation) {
      setLastTabIndex(null);
    }
  }, [lastLocation]);

  useEffect((): void => {
    if (contactUserId) {
      resetContactUserForm(contactUserData);
    }
  }, [contactUserId]);

  useEffect((): void => {
    if (contactPersonData && isContactModalOpen) {
      populateContactData(contactPersonData, formPrefixNewContact, true);
    }
  }, [contactPersonData]);

  useEffect((): void => {
    if (contactVCardFile && contactVCardFile.link) {
      ScreenService.downloadFile(contactVCardFile.link);
      dispatch(resetContactVCardData());
    }
  }, [contactVCardFile]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEffect((): void => {
    if (contactData) {
      const initialContactAddressItems: Record<string, ContactAddressInput> = {};
      const initialContactAddressDetailsItems: number[] = [];
      if (contactData.addresses && contactData.addresses.length) {
        contactData.addresses.forEach((contactAddressItem: ContactAddress): void => {
          initialContactAddressItems[contactAddressItem.id] = {
            id: contactAddressItem.id,
            address: contactAddressItem.address,
            addressDetails: contactAddressItem.addressDetails,
            name: contactAddressItem.name,
            streetAddress: contactAddressItem.streetAddress,
            location: contactAddressItem.location,
            postalCode: contactAddressItem.postalCode,
          };

          if (contactAddressItem.addressDetails) {
            initialContactAddressDetailsItems.push(contactAddressItem.id);
          }
        });
      }
      setContactAddressItems(initialContactAddressItems);
      setContactAddressDetailsItems(initialContactAddressDetailsItems);
    }
  }, [contactData, isEditMode]);

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  if (errorMessage) {
    return (
      <PageError message={errorMessage} />
    );
  }

  if (!contactId) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  if (!isDataLoaded) {
    return <Loader isPageLoader />;
  }

  return (
    <CheckAccess permissions={contactViewPermissions}>
      <Form id={FORM_ID_CONTACT} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
          <ContentHeader title={pageTitle} />
          <Controls
            isHidden={isControlHidden}
            isSaveDisabled={isSaveDisabled}
            withDelete={queryTab !== TAB_NAME_ASSOCIATED_DEALS}
            withNoEditMode={queryTab === TAB_NAME_ASSOCIATED_DEALS}
            createButtonTitle={createButtonTitle}
            editPermissions={contactEditPermissions}
            createPermission={CONTACT_CREATE}
            deletePermission={CONTACT_DELETE}
            onCancelForm={onCancelForm}
            onCreateButtonClick={onCreateButtonClick}
            onSubmitForm={onSubmitForm}
            onDeleteClick={onDeleteClick}
          />
          <LimitedPageContainer isActive>
            <PageTabs
              isDisabled={isFormChanged}
              isCompressed={isRightDrawerOpen}
              tabs={tabs}
              currentTab={queryTab}
              onTabChange={onTabChange}
              onBeforeTabChange={onBeforeTabChange}
            />
          </LimitedPageContainer>
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError={isEditMode}
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <ContactModal
          isContactPageForm
          isOpen={isContactModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          contactPhotoPreviewUrl={newContactPhotoPreviewUrl}
          onCloseModal={closeContactModal}
          contactCompanyList={contactCompanyList}
          contactPhotoFile={newContactPhotoFile}
          contactGroupList={contactGroupList}
          contactTypeList={contactTypeList}
          assetTypeList={assetTypeList}
          tagList={tagList}
          saveFormDeps={saveFormDeps}
          onCreate={onNewContactCreate}
          onCancel={onCancelForm}
          onSubmitCreateForm={onContactSubmitCreateForm}
          onPhoneChange={onNewContactPhoneChange}
          onPhoneWorkChange={onNewContactPhoneWorkChange}
          onAddressSearchCompleted={onNewContactAddressSearchCompleted}
          onAddressFieldKeyDown={onNewContactAddressFieldKeyDown}
          onAssetTypesChange={onNewContactAssetTypesChange}
          onContactPhotoChange={onNewContactPhotoFileChange}
          onContactPhotoDelete={onNewContactPhotoFileDelete}
          onContactGroupsChange={onNewContactGroupsChange}
          onEmailChange={onNewContactEmailChange}
          onTagsChange={onNewContactTagsChange}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onTagSearch={onTagSearch}
          onTagSearchClose={onTagSearchClose}
        />
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onCloseModal={closeLeaveConfirmModal}
          onConfirm={onLeaveConfirm}
        />
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeleteConfirm}
          onCloseModal={closeDeleteConfirmModal}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    contact,
    contactList,
    controls,
    rightDrawer,
  } = reducers;

  const contactData: Contact = contact.contactData && contact.contactData.contact;
  const contactRelatedContactList: Contact = contact.contactData && contact.contactData.relatedContactList;

  if (contactData) {
    initialValues.contactData = {
      id: contactData.id,
      userId: contactData.user ? contactData.user.id : null,
      name: contactData.name,
      firstName: contactData.firstName,
      lastName: contactData.lastName,
      companyId: contactData.company ? contactData.company.id : null,
      type: contactData.type,
      groupIds: contactData.groups && contactData.groups.length ? contactData.groups : [],
      phone: contactData.phone,
      phoneExtension: contactData.phoneExtension,
      phoneWork: contactData.phoneWork,
      phoneWorkExtension: contactData.phoneWorkExtension,
      email: contactData.email,
      streetAddress: contactData.streetAddress,
      addressDetails: contactData.addressDetails,
      location: contactData.location,
      postalCode: contactData.postalCode,
      linkedin: contactData.linkedin,
      title: contactData.title,
      description: contactData.description,
      industry: contactData.industry,
      foundationYear: contactData.foundationYear,
      headQuarter: contactData.headQuarter,
      website: contactData.website,
      keySubsidiaries: contactData.keySubsidiaries,
      tags: contactData.tags,
      assetTypes: contactData.assetTypes,
      address: contactData.address,
      isDefault: contactData.isDefault,
      relatedContactIds: contactRelatedContactList,
      photoLink: null,
      photo: null,
      addresses: {},
      newContact: {},
    };

    if (contactData.addresses && contactData.addresses.length) {
      contactData.addresses.forEach((contactAddress: ContactAddress): void => {
        const formKeyContactAddress: string = FormService.getIndexedFormFieldKey(contactAddress.id);

        initialValues.contactData.addresses[formKeyContactAddress] = {
          id: contactAddress.id,
          name: contactAddress.name,
          address: contactAddress.address,
          addressDetails: contactAddress.addressDetails,
          streetAddress: contactAddress.streetAddress,
          location: contactAddress.location,
          postalCode: contactAddress.postalCode,
        };
      });
    }
  }

  return {
    isDataLoaded: !!contact.contactData,
    isEditMode: controls.isEditMode,
    isCommentSaving: contact.isCommentSaving,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    isRelatedContactListLoading: contact.isRelatedContactListLoading,
    isContactDealListLoading: contact.isContactDealListLoading,
    currentUser: app.baseData && app.baseData.currentUser,
    contactVCardFile: contact.contactData && contact.contactVCardFile,
    contactData: contact.contactData && contact.contactData.contact,
    contactPersonData: contactList.contactPersonData,
    contactCompanyData: contactList.contactCompanyData,
    contactDealList: contact.contactData && contact.contactData.contactDealList,
    contactDealsTotal: contact.contactData && contact.contactData.contactDealsTotal,
    relatedContactList: contactRelatedContactList,
    relatedContactsTotal: contact.contactData && contact.contactData.relatedContactsTotal,
    contactUserData: contact.contactData && contact.contactData.contactUser,
    contactTypeList: contact.contactData && contact.contactData.contactTypeList,
    contactPersonList: contact.contactData && contact.contactData.contactPersonList,
    contactCompanyList: contact.contactData && contact.contactData.contactCompanyList,
    contactGroupList: contact.contactData && contact.contactData.contactGroupList,
    assetTypeList: contact.contactData && contact.contactData.assetTypeList,
    commentLabelList: contact.contactData && contact.contactData.commentLabelList,
    tagList: contact.contactData && contact.contactData.tagList,
    contactLinkedUserList: contact.contactData && contact.contactData.contactLinkedUserList,
    notLinkedUserList: contact.contactData && contact.contactData.notLinkedUserList,
  };
};

const ContactForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ contact }: Reducers): ValidationErrorsMap => (
    contact.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ contact }: Reducers): ValidationErrorsMap => (
    contact.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_CONTACT}
      component={Contact}
      validator={ContactValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(ContactForm);
